import { createContext, useEffect, useState, useContext } from "react";
import {
  getProducts,
  getProductsByCustomerGroup,
  getProductsByPriceListCode,
} from "../utils/ProductsApi";
import { UserContext } from "./user.context";

export const ProductsContext = createContext({
  products: [],
  getMyProducts: () => null,
  getMyProductsByCustomerGroup: () => null,
  getProductsListByPriceCode: (listCode) => null,
  reCountProductsQuantity: () => null,
  increaseQuantity: () => null,
  reduceQuantity: () => null,
  promoCodeApplied: false,
});

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);

  const { currentUser } = useContext(UserContext);
  const isLoggedIn = !!currentUser?.isloggedIn;

  // Merge old quantities into the new fetched list
  const mergeQuantities = (newList, oldList) => {
    const qtyMap = new Map(
      (oldList || []).map((p) => [String(p.id), Number(p.quantity ?? 0)])
    );

    return (newList || []).map((p) => ({
      ...p,
      quantity: qtyMap.get(String(p.id)) ?? Number(p.quantity ?? 0),
    }));
  };

  // products.context.js
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // ✅ Guests: fetch public products only
        // ✅ Logged-in: fetch customer-group merged products
        const fetched = isLoggedIn
          ? await getProductsByCustomerGroup()
          : await getProducts();

        if (cancelled) return;

        setProducts((prev) =>
          Array.isArray(fetched) ? mergeQuantities(fetched, prev) : prev
        );
      } catch (e) {
        console.error("Failed to load products", e);

        // ✅ Final fallback: public products
        try {
          const general = await getProducts();
          if (cancelled) return;

          setProducts((prev) =>
            Array.isArray(general) ? mergeQuantities(general, prev) : prev
          );
        } catch (e2) {
          console.error("Failed to load public products", e2);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const getMyProducts = async () => {
    const general = await getProducts();
    setProducts((prev) =>
      Array.isArray(general) ? mergeQuantities(general, prev) : prev
    );
  };

  const getMyProductsByCustomerGroup = async () => {
    const mergedOrGeneral = await getProductsByCustomerGroup();
    setProducts((prev) =>
      Array.isArray(mergedOrGeneral)
        ? mergeQuantities(mergedOrGeneral, prev)
        : prev
    );
  };

  const getProductsListByPriceCode = async (listCode) => {
    const res = await getProductsByPriceListCode({ listCode });
    const list = res?.data ?? res;

    setProducts((prev) =>
      Array.isArray(list) ? mergeQuantities(list, prev) : prev
    );
  };

  // Quantity updates (immutable)
  const reCountProductsQuantity = (productToRemove) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === productToRemove.id ? { ...p, quantity: 0 } : p))
    );
  };

  const reduceQuantity = (productToDecrease) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id !== productToDecrease.id) return p;
        const nextQty = (p.quantity ?? 0) - 1;
        return { ...p, quantity: nextQty < 0 ? 0 : nextQty };
      })
    );
  };

  const increaseQuantity = (productToIncrease) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === productToIncrease.id
          ? { ...p, quantity: (p.quantity ?? 0) + 1 }
          : p
      )
    );
  };

  const value = {
    products,
    getMyProducts,
    getMyProductsByCustomerGroup,
    getProductsListByPriceCode,
    reCountProductsQuantity,
    increaseQuantity,
    reduceQuantity,
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
};
